<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import ThemeButton from '@/components/ThemeButton.vue'
import RemoteImage from '@/components/RemoteImage.vue'

defineOptions({ name: 'CustomDialog' })

withDefaults(
  defineProps<{
    visible: boolean
    image?: unknown
    title?: string
    content?: unknown
    closeOnMaskClick?: boolean
    showCancelButton?: boolean
    cancelText?: string
    confirmText?: string
    confirmDisabled?: boolean
    // 层级：不传则用 vant 默认（2000 起递增）；传值可固定覆盖层级
    zIndex?: number
  }>(),
  { closeOnMaskClick: false, showCancelButton: true },
)

const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'confirm'): void
}>()

const { t } = useI18n()
</script>

<template>
  <!-- teleport="body"：vant 组件式 dialog 默认 teleport=undefined 不会传送，
       会留在调用处（PageLayout 内容区内）。内容区 overflow-y-auto + z-10 创建层叠上下文，
       导致弹窗在 WebView 中被困于容器内、无法覆盖顶部 NavBar。显式传送至 body 即可全屏覆盖。 -->
  <van-dialog :show="visible" teleport="body" :show-confirm-button="false" :show-cancel-button="false"
    :close-on-click-overlay="closeOnMaskClick" :z-index="zIndex" :width="'calc(100vw - 56px)'"
    @click-overlay="closeOnMaskClick && emit('cancel')">
    <div class="w-full p-6">
      <div v-if="image" class="flex justify-center mb-4">
        <component :is="image" v-if="typeof image === 'object'" />
        <RemoteImage v-else :src="image as string" alt="" />
      </div>

      <div v-if="title" class="text-lg font-medium text-ink/90 text-center leading-tight">
        {{ title }}
      </div>

      <div v-if="$slots.default" class="pt-4 text-center">
        <slot />
      </div>
      <div v-else-if="content" class="pt-4 text-center text-ink/50">
        <component :is="content" v-if="typeof content === 'object'" />
        <span v-else>{{ content }}</span>
      </div>

      <div class="flex gap-3 pt-6">
        <ThemeButton v-if="showCancelButton" variant="secondary" block class="flex-1 min-w-0" @click="emit('cancel')">
          {{ cancelText || t('cancel') }}
        </ThemeButton>
        <ThemeButton block class="flex-1 min-w-0" :disabled="confirmDisabled" @click="emit('confirm')">
          {{ confirmText || t('confirm') }}
        </ThemeButton>
      </div>
    </div>
  </van-dialog>
</template>
